import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Sidebar, Header } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Car, Wrench, DollarSign, Megaphone, Box, Headphones, MessageSquare,
  ChevronRight, TrendingUp, Users, Calendar, Target, Brain, Sparkles
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  const VERTICALS = [
    { icon: Car, title: "Sales", path: "/sales", color: "text-blue-500", bgColor: "bg-blue-500/10", desc: "Lead management & vehicle matching" },
    { icon: Wrench, title: "Service", path: "/service", color: "text-emerald-500", bgColor: "bg-emerald-500/10", desc: "Appointment scheduling & repairs" },
    { icon: DollarSign, title: "F&I", path: "/fi", color: "text-amber-500", bgColor: "bg-amber-500/10", desc: "Finance calculations & products" },
    { icon: Megaphone, title: "Marketing", path: "/marketing", color: "text-purple-500", bgColor: "bg-purple-500/10", desc: "Campaign management & analytics" },
    { icon: Box, title: "Parts", path: "/parts", color: "text-orange-500", bgColor: "bg-orange-500/10", desc: "Inventory & order management" },
    { icon: Headphones, title: "Support", path: "/support", color: "text-teal-500", bgColor: "bg-teal-500/10", desc: "Customer support & inquiries" },
  ];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground" data-testid="dashboard-title">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground">Your AI-powered dealership command center</p>
          </div>
          <Button variant="outline" onClick={() => setShowChat(true)} className="gap-2" data-testid="dashboard-ai-btn">
            <Brain className="w-4 h-4" />
            AI Assistant
          </Button>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Leads", value: stats?.total_leads || 0, icon: Users, color: "text-blue-500", change: "+12%" },
            { label: "Qualified Leads", value: stats?.qualified_leads || 0, icon: Target, color: "text-emerald-500", change: "+8%" },
            { label: "Appointments Today", value: stats?.appointments_today || 0, icon: Calendar, color: "text-amber-500", change: "+5" },
            { label: "Active Campaigns", value: stats?.active_campaigns || 0, icon: Megaphone, color: "text-purple-500", change: "+2" },
          ].map((stat, idx) => (
            <Card key={idx} className="relative overflow-hidden" data-testid={`stat-${idx}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <Badge variant="secondary" className="text-emerald-600 bg-emerald-50">{stat.change}</Badge>
                </div>
                <div className="text-3xl font-display font-bold text-foreground">{loading ? '-' : stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* AI Agents Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              AI Agents
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VERTICALS.map((v, idx) => (
              <Card 
                key={idx} 
                className="group cursor-pointer hover:border-blue-500/30 transition-all hover:-translate-y-1"
                onClick={() => navigate(v.path)}
                data-testid={`vertical-${v.title.toLowerCase()}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${v.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <v.icon className={`w-6 h-6 ${v.color}`} />
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground mb-1">{v.title}</h3>
                  <p className="text-sm text-muted-foreground">{v.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Performance Overview */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Key Metrics */}
          <Card data-testid="metrics-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: "Lead Conversion Rate", value: `${stats?.conversion_rate || 0}%`, progress: stats?.conversion_rate || 0 },
                { label: "Customer Satisfaction", value: `${stats?.customer_satisfaction || 0}/5`, progress: ((stats?.customer_satisfaction || 0) / 5) * 100 },
                { label: "Revenue MTD", value: `$${(stats?.revenue_mtd || 0).toLocaleString()}`, progress: 75 },
              ].map((metric, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{metric.label}</span>
                    <span className="font-semibold text-foreground">{loading ? '-' : metric.value}</span>
                  </div>
                  <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${metric.progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card data-testid="actions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Car, label: "Add Lead", path: "/sales", color: "bg-blue-500" },
                  { icon: Calendar, label: "Schedule Service", path: "/service", color: "bg-emerald-500" },
                  { icon: DollarSign, label: "Calculate Payment", path: "/fi", color: "bg-amber-500" },
                  { icon: Megaphone, label: "Create Campaign", path: "/marketing", color: "bg-purple-500" },
                  { icon: Box, label: "Check Inventory", path: "/parts", color: "bg-orange-500" },
                  { icon: MessageSquare, label: "AI Chat", action: () => setShowChat(true), color: "bg-teal-500" },
                ].map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => action.action ? action.action() : navigate(action.path)}
                    className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-blue-500/30 hover:bg-blue-500/5 transition-all group"
                    data-testid={`action-${idx}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <action.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-medium text-sm text-foreground">{action.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low Stock Alert */}
        {stats?.parts_low_stock > 0 && (
          <Card className="mt-6 border-amber-500/50" data-testid="low-stock-alert">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Box className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <span className="font-medium text-foreground">{stats.parts_low_stock} parts running low</span>
                <span className="text-sm text-muted-foreground ml-2">Consider reordering soon</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/parts')}>
                View Parts
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} />
      
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          style={{ zIndex: 9998 }}
          data-testid="chat-fab"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
